import { useEffect, useMemo, useRef, useState } from "react";
import GameLauncher from "../shared/GameLauncher";
import GameProgress from "../shared/GameProgress";
import GameResults from "../shared/GameResults";
import GameInterpretation from "../shared/GameInterpretation.jsx";
import { getBasicInterpretation } from "../shared/gameInterpretation";
import "./ObjectDetection.css";

const SYMBOLS = [
  "🍎",
  "🍌",
  "🍇",
  "🍓",
  "🍊",
  "🥕",
  "🌽",
  "🐶",
  "🐱",
  "🐰",
  "🐸",
  "🐼",
  "🚗",
  "🚲",
  "🚀",
  "✈️",
  "⚽",
  "🏀",
  "🎈",
  "🎁",
  "📚",
  "✏️",
  "📦",
  "🧸",
  "⭐",
  "🌸",
  "🦋",
  "🎵",
  "🔑",
  "☂️",
  "🧩",
  "🪁",
  "🥄",
  "🧦",
  "🧢",
  "🪥",
  "📷",
  "⌚",
  "💡",
  "✂️",
];

const TARGET_SYMBOLS = [
  "✂️",
  "🚗",
  "🍎",
  "⭐",
  "🐶",
  "🎈",
  "🔑",
  "📚",
  "⚽",
  "🦋",
];

const GAME_CONFIG = {
  easy: {
    label: "Fácil",
    totalRounds: 8,
    itemCount: 9,
    columns: 3,
    displayTime: 3000,
  },
  medium: {
    label: "Media",
    totalRounds: 10,
    itemCount: 16,
    columns: 4,
    displayTime: 2000,
  },
  hard: {
    label: "Difícil",
    totalRounds: 12,
    itemCount: 25,
    columns: 5,
    displayTime: 1000,
  },
};

function randomItem(list) {
  return list[Math.floor(Math.random() * list.length)];
}

function shuffle(list) {
  const copy = [...list];

  for (let index = copy.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [copy[index], copy[randomIndex]] = [copy[randomIndex], copy[index]];
  }

  return copy;
}

function createRound(config) {
  const target = randomItem(TARGET_SYMBOLS);
  const targetPresent = Math.random() < 0.5;

  const distractors = SYMBOLS.filter((symbol) => symbol !== target);
  const requiredDistractors = targetPresent
    ? config.itemCount - 1
    : config.itemCount;

  const items = [];

  while (items.length < requiredDistractors) {
    items.push(randomItem(distractors));
  }

  if (targetPresent) {
    items.push(target);
  }

  return {
    target,
    targetPresent,
    items: shuffle(items),
  };
}

export default function ObjectDetection() {
  const [selectedDifficulty, setSelectedDifficulty] = useState(null);
  const [config, setConfig] = useState(null);

  const [roundIndex, setRoundIndex] = useState(0);
  const [round, setRound] = useState(null);
  const [phase, setPhase] = useState("launcher");

  const [correct, setCorrect] = useState(0);
  const [falseAlarms, setFalseAlarms] = useState(0);
  const [omissions, setOmissions] = useState(0);
  const [responses, setResponses] = useState([]);

  const [startTime, setStartTime] = useState(null);
  const [endTime, setEndTime] = useState(null);
  const [answerStartTime, setAnswerStartTime] = useState(null);

  const displayTimerRef = useRef(null);

  const totalRounds = config?.totalRounds || 0;
  const finished = phase === "finished";
  const started = phase === "preview" || phase === "answer";

  const progress = useMemo(() => {
    if (!totalRounds) return 0;
    return Math.round((roundIndex / totalRounds) * 100);
  }, [roundIndex, totalRounds]);

  const accuracy =
    totalRounds > 0 ? Math.round((correct / totalRounds) * 100) : 0;

  const averageReactionTime =
    responses.length > 0
      ? Math.round(
          responses.reduce((total, response) => {
            return total + response.reactionTime;
          }, 0) / responses.length
        )
      : 0;

  const totalTime =
    startTime && endTime ? Math.round((endTime - startTime) / 1000) : 0;

  const interpretation = finished
    ? getBasicInterpretation({
        accuracy,
        averageReactionTime,
      })
    : null;

  const clearDisplayTimer = () => {
    if (displayTimerRef.current) {
      clearTimeout(displayTimerRef.current);
      displayTimerRef.current = null;
    }
  };

  const showRound = (nextRound, nextConfig) => {
    clearDisplayTimer();

    setRound(nextRound);
    setPhase("preview");
    setAnswerStartTime(null);

    displayTimerRef.current = setTimeout(() => {
      setPhase("answer");
      setAnswerStartTime(Date.now());
    }, nextConfig.displayTime);
  };

  const startGame = (difficulty = selectedDifficulty || "easy") => {
    const nextConfig = GAME_CONFIG[difficulty] || GAME_CONFIG.easy;
    const now = Date.now();

    clearDisplayTimer();

    setSelectedDifficulty(difficulty);
    setConfig(nextConfig);
    setRoundIndex(0);
    setCorrect(0);
    setFalseAlarms(0);
    setOmissions(0);
    setResponses([]);
    setStartTime(now);
    setEndTime(null);

    showRound(createRound(nextConfig), nextConfig);
  };

  const answer = (answerValue) => {
    if (
      phase !== "answer" ||
      !round ||
      !config ||
      answerStartTime === null
    ) {
      return;
    }

    const now = Date.now();
    const reactionTime = now - answerStartTime;
    const isCorrect = answerValue === round.targetPresent;

    const isFalseAlarm = answerValue === true && !round.targetPresent;
    const isOmission = answerValue === false && round.targetPresent;

    setResponses((previous) => [
      ...previous,
      {
        round: roundIndex + 1,
        target: round.target,
        targetPresent: round.targetPresent,
        answer: answerValue,
        isCorrect,
        falseAlarm: isFalseAlarm,
        omission: isOmission,
        reactionTime,
      },
    ]);

    if (isCorrect) {
      setCorrect((previous) => previous + 1);
    }

    if (isFalseAlarm) {
      setFalseAlarms((previous) => previous + 1);
    }

    if (isOmission) {
      setOmissions((previous) => previous + 1);
    }

    const nextRoundIndex = roundIndex + 1;

    if (nextRoundIndex >= config.totalRounds) {
      clearDisplayTimer();
      setPhase("finished");
      setEndTime(now);
      return;
    }

    setRoundIndex(nextRoundIndex);
    showRound(createRound(config), config);
  };

  const backToLauncher = () => {
    clearDisplayTimer();

    setSelectedDifficulty(null);
    setConfig(null);
    setRound(null);
    setRoundIndex(0);
    setPhase("launcher");
    setAnswerStartTime(null);
  };

  useEffect(() => {
    return () => {
      clearDisplayTimer();
    };
  }, []);

  if (phase === "launcher") {
    return (
      <GameLauncher
        title="Detección de objetos"
        description="Observa los objetos durante unos segundos y responde si apareció el objeto objetivo."
        areas={[
          "Atención focalizada",
          "Atención selectiva",
          "Atención sostenida",
          "Escaneo visual",
        ]}
        defaultDifficulty="easy"
        onStart={startGame}
      />
    );
  }

  return (
    <div className="object-detection-page">
      <div className="object-detection-card">
        <header className="object-detection-header">
          <div>
            <h1>Detección de objetos</h1>
            <p>
              Dificultad: <strong>{config?.label}</strong>
            </p>
          </div>

          <button
            type="button"
            onClick={() => startGame(selectedDifficulty)}
          >
            Reiniciar
          </button>
        </header>

        {started && round && (
          <>
            <GameProgress
              label="Ronda"
              current={roundIndex + 1}
              total={totalRounds}
              progress={progress}
            />

            <section className="object-target-box">
              <span>Busca este objeto</span>
              <strong>{round.target}</strong>
            </section>

            {phase === "preview" && (
              <>
                <p className="object-detection-instruction">
                  Observa con atención
                </p>

                <section
                  className="object-detection-grid"
                  style={{
                    gridTemplateColumns: `repeat(${config.columns}, 1fr)`,
                  }}
                >
                  {round.items.map((symbol, index) => (
                    <div
                      key={`${symbol}-${index}`}
                      className="object-detection-item"
                    >
                      {symbol}
                    </div>
                  ))}
                </section>

                <div className="object-detection-waiting">
                  Memoriza los objetos...
                </div>
              </>
            )}

            {phase === "answer" && (
              <section className="object-answer-section">
                <h2>¿Aparecía el objeto?</h2>

                <div className="object-answer-buttons">
                  <button
                    type="button"
                    className="yes-button"
                    onClick={() => answer(true)}
                  >
                    Sí
                  </button>

                  <button
                    type="button"
                    className="no-button"
                    onClick={() => answer(false)}
                  >
                    No
                  </button>
                </div>
              </section>
            )}

            <section className="object-detection-score">
              <span>Aciertos: {correct}</span>
              <span>Falsas alarmas: {falseAlarms}</span>
              <span>Omisiones: {omissions}</span>
              <span>Precisión: {accuracy}%</span>
            </section>
          </>
        )}

        {finished && (
          <>
            <GameResults
              results={[
                { label: "Aciertos", value: correct },
                { label: "Falsas alarmas", value: falseAlarms },
                { label: "Omisiones", value: omissions },
                { label: "Precisión", value: `${accuracy}%` },
                {
                  label: "Tiempo medio",
                  value: `${averageReactionTime} ms`,
                },
                { label: "Tiempo total", value: `${totalTime}s` },
              ]}
              onRepeat={() => startGame(selectedDifficulty)}
              onChangeDifficulty={backToLauncher}
            />

            <GameInterpretation interpretation={interpretation} />
          </>
        )}
      </div>
    </div>
  );
}